if (!customElements.get('get-the-look')) {
  customElements.define(
    'get-the-look',
    class GetTheLook extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.activeIndex = 0;

        // No custom wheel/touch handling on the carousel either: it's a
        // plain overflow-x:auto + scroll-snap track (see .get-the-look__track),
        // so desktop trackpads get real native momentum and can chain swipes
        // freely, exactly like the product photo gallery and the modal below.

        this.modal = this.querySelector('[data-look-modal]');
        this.modalImages = this.querySelector('[data-modal-images]');
        this.bars = Array.from(this.querySelectorAll('.get-the-look__modal-bar'));
        this.compositions = Array.from(this.querySelectorAll('[data-composition]'));
        this.ctaBtn = this.querySelector('[data-look-add-to-cart]');

        this.pills = [];
        this.querySelectorAll('[data-look-open]').forEach((openBtn) => {
          const index = parseInt(openBtn.dataset.lookIndex, 10);
          this.pills[index] = openBtn;
          const slide = openBtn.closest('.get-the-look__slide') || openBtn;
          slide.addEventListener('click', () => this.openModal(index));
        });

        this.compositions.forEach((panel, index) => this.initComposition(panel, index));

        if (this.modal) {
          const closeBtn = this.modal.querySelector('[data-look-close]');
          closeBtn.addEventListener('click', () => this.closeModal());
          this.modal.addEventListener('click', (event) => {
            if (event.target === this.modal) this.closeModal();
          });
        }

        if (this.modalImages) {
          let ticking = false;
          this.modalImages.addEventListener(
            'scroll',
            () => {
              if (ticking) return;
              ticking = true;
              requestAnimationFrame(() => {
                this.syncActiveIndex();
                ticking = false;
              });
            },
            { passive: true }
          );

          // No custom wheel handling: overflow-x:auto + scroll-snap-type
          // already lets desktop trackpads swipe this natively (exactly like
          // the product photo gallery) — intercepting wheel ourselves only
          // fought that native feel and made it feel janky.
        }

        if (this.ctaBtn) {
          this.ctaBtn.addEventListener('click', () => this.addToCart(this.activeIndex));
        }

        this.compositions.forEach((panel, index) => this.recompute(index));
      }

      initComposition(panel, index) {
        const items = Array.from(panel.querySelectorAll('[data-item]'));

        items.forEach((item) => {
          const checkbox = item.querySelector('[data-item-checkbox]');
          checkbox.addEventListener('change', () => this.recompute(index));

          const select = item.querySelector('[data-item-variant-select]');
          if (select) {
            const label = item.querySelector('[data-select-label]');
            select.addEventListener('change', () => {
              if (label && select.selectedIndex > -1) {
                label.textContent = select.options[select.selectedIndex].textContent.trim();
              }
              this.recompute(index);
            });
          }
        });
      }

      getItemState(item) {
        const checkbox = item.querySelector('[data-item-checkbox]');
        const priceEl = item.querySelector('[data-item-price]');
        const select = item.querySelector('[data-item-variant-select]');
        const hiddenVariant = item.querySelector('[data-item-variant-id]');
        const eligible = item.dataset.eligible === 'true';

        let variantId = null;
        let ready = true;
        let price = parseInt(priceEl.dataset.price || '0', 10);

        if (select) {
          variantId = select.value || null;
          if (!variantId) {
            ready = false;
          } else {
            price = parseInt(select.selectedOptions[0].dataset.price || '0', 10);
          }
        } else if (hiddenVariant) {
          variantId = hiddenVariant.value;
        }

        return {
          checked: checkbox.checked,
          eligible,
          price,
          variantId,
          ready,
        };
      }

      // Mirrors Shopify's "Buy 1 Get 1 free" automatic discount: among the
      // checked & eligible items, the cheapest half (rounded down) are free.
      computeTotals(states) {
        const checkedIndices = [];
        states.forEach((state, index) => {
          if (state.checked) checkedIndices.push(index);
        });

        const compareTotal = checkedIndices.reduce((sum, i) => sum + states[i].price, 0);

        const eligibleIndices = checkedIndices.filter((i) => states[i].eligible);
        const freeCount = Math.floor(eligibleIndices.length / 2);
        const cheapestFirst = [...eligibleIndices].sort((a, b) => states[a].price - states[b].price);
        const freeIndices = new Set(cheapestFirst.slice(0, freeCount));

        let total = 0;
        checkedIndices.forEach((i) => {
          if (!freeIndices.has(i)) total += states[i].price;
        });

        return {
          total,
          compareTotal,
          savings: compareTotal - total,
          offeredCount: freeIndices.size,
          freeIndices,
          allReady: checkedIndices.every((i) => states[i].ready),
          hasChecked: checkedIndices.length > 0,
        };
      }

      recompute(index) {
        const panel = this.compositions[index];
        const pill = this.pills[index];
        if (!panel) return;

        const items = Array.from(panel.querySelectorAll('[data-item]'));
        const states = items.map((item) => this.getItemState(item));
        const totals = this.computeTotals(states);

        const itemCountEl = panel.querySelector('[data-item-count]');
        if (itemCountEl) {
          const checkedCount = states.filter((state) => state.checked).length;
          itemCountEl.textContent = formatItemCount(checkedCount);
        }

        const savingsLabel = totals.offeredCount > 0 ? formatSavings(totals.savings, totals.offeredCount) : '';

        if (pill) {
          const pillPrice = pill.querySelector('[data-pill-price]');
          const pillComparePrice = pill.querySelector('[data-pill-compare-price]');
          const pillSavings = pill.querySelector('[data-pill-savings]');

          pillPrice.textContent = formatMoney(totals.total);
          pillComparePrice.hidden = totals.savings <= 0;
          if (totals.savings > 0) pillComparePrice.textContent = formatMoney(totals.compareTotal);
          pillSavings.hidden = totals.savings <= 0;
          if (totals.savings > 0) pillSavings.textContent = savingsLabel;
        }

        items.forEach((item, i) => {
          const state = states[i];
          const priceEl = item.querySelector('[data-item-price]');
          const isFree = state.checked && totals.freeIndices.has(i);
          item.classList.toggle('get-the-look__item--free', isFree);

          if (isFree) {
            priceEl.innerHTML = '';
            const free = document.createElement('span');
            free.className = 'get-the-look__item-price-free';
            free.textContent = window.getTheLookStrings.free;
            const strike = document.createElement('span');
            strike.className = 'get-the-look__item-price-strike';
            strike.textContent = formatMoney(state.price);
            priceEl.append(free, strike);
          } else {
            priceEl.textContent = formatMoney(state.price);
          }
        });

        if (index === this.activeIndex) this.updateFooter(totals, savingsLabel);
      }

      updateFooter(totals, savingsLabel) {
        if (!this.ctaBtn) return;
        const modal = this.modal;
        const ctaLabel = modal.querySelector('[data-cta-label]');
        const ctaPrice = modal.querySelector('[data-cta-price]');
        const ctaComparePrice = modal.querySelector('[data-cta-compare-price]');
        const modalSavings = modal.querySelector('[data-modal-savings]');

        modalSavings.hidden = totals.savings <= 0;
        if (totals.savings > 0) modalSavings.textContent = savingsLabel;

        ctaPrice.textContent = formatMoney(totals.total);
        ctaComparePrice.hidden = totals.savings <= 0;
        if (totals.savings > 0) ctaComparePrice.textContent = formatMoney(totals.compareTotal);

        if (!totals.hasChecked) {
          this.ctaBtn.disabled = true;
          ctaLabel.textContent = window.getTheLookStrings.selectAtLeastOne;
        } else if (!totals.allReady) {
          this.ctaBtn.disabled = true;
          ctaLabel.textContent = window.getTheLookStrings.chooseSizes;
        } else {
          this.ctaBtn.disabled = false;
          ctaLabel.textContent = window.getTheLookStrings.addToCart;
        }
      }

      setActiveIndex(index) {
        this.activeIndex = index;

        this.bars.forEach((bar, i) => {
          bar.classList.toggle('get-the-look__modal-bar--active', i === index);
        });

        this.compositions.forEach((panel, i) => {
          panel.hidden = i !== index;
        });

        this.recompute(index);
      }

      // Reads the currently centered slide from native scroll position —
      // the same mechanism the product photo gallery itself relies on.
      syncActiveIndex() {
        if (!this.modalImages) return;
        const width = this.modalImages.clientWidth;
        if (!width) return;
        const index = Math.round(this.modalImages.scrollLeft / width);
        if (index !== this.activeIndex) this.setActiveIndex(index);
      }

      openModal(index) {
        this.modal.hidden = false;
        document.body.classList.add('get-the-look-modal-open');

        // Wait a frame so the just-unhidden modal has real layout before we
        // read clientWidth / scroll it — otherwise the first swipe right
        // after opening can be measured against a stale (zero) width.
        requestAnimationFrame(() => {
          if (this.modalImages) {
            this.modalImages.scrollTo({ left: index * this.modalImages.clientWidth, behavior: 'instant' });
          }
          this.setActiveIndex(index);
          this.modal.classList.add('get-the-look__modal--active');
        });
      }

      closeModal() {
        this.modal.classList.remove('get-the-look__modal--active');
        document.body.classList.remove('get-the-look-modal-open');
        setTimeout(() => {
          this.modal.hidden = true;
        }, 300);

        // Reset every item back to checked so each pill always shows the
        // "everything included" price once the composition is closed.
        this.compositions.forEach((panel, index) => {
          panel.querySelectorAll('[data-item-checkbox]').forEach((checkbox) => {
            checkbox.checked = true;
          });
          this.recompute(index);
        });
      }

      addToCart(index) {
        const panel = this.compositions[index];
        if (!panel || this.ctaBtn.disabled) return;

        const items = Array.from(panel.querySelectorAll('[data-item]'));
        const states = items.map((item) => this.getItemState(item));
        const toAdd = states.filter((state) => state.checked && state.variantId);
        if (toAdd.length === 0) return;

        this.ctaBtn.classList.add('get-the-look__modal-cta--loading');
        this.ctaBtn.setAttribute('aria-disabled', 'true');

        const config = fetchConfig('json');
        config.body = JSON.stringify({
          items: toAdd.map((state) => ({ id: state.variantId, quantity: 1 })),
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              throw new Error(response.description || 'Cart add error');
            }
            this.closeModal();
            if (this.cart && typeof this.cart.renderContents === 'function') {
              try {
                this.cart.renderContents(response);
              } catch (renderError) {
                console.error('Get the look – cart render error:', renderError);
              }
            }
          })
          .catch((error) => {
            console.error('Get the look – add to cart error:', error);
          })
          .finally(() => {
            this.ctaBtn.classList.remove('get-the-look__modal-cta--loading');
            this.ctaBtn.removeAttribute('aria-disabled');
          });
      }
    }
  );
}

function formatMoney(cents) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const locale = document.documentElement.lang || 'fr';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch (error) {
    const amount = (Math.round(cents) / 100).toFixed(2).replace('.', ',');
    return `${amount}€`;
  }
}

function formatItemCount(count) {
  const template = count === 1 ? window.getTheLookStrings.itemCountOne : window.getTheLookStrings.itemCountOther;
  return template.replace('[count]', count);
}

function formatSavings(cents, count) {
  const template = count === 1 ? window.getTheLookStrings.savingsOne : window.getTheLookStrings.savingsOther;
  return template.replace('[amount]', formatMoney(cents)).replace('[count]', count);
}
